import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { playersService } from '../../../services/supabaseService';

const GlobalImportPlayersModal = ({ isOpen, onClose, teams, onRefresh }) => {
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e?.target?.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile?.name?.endsWith('.csv')) {
      setError('Please select a valid CSV file');
      return;
    }

    setFile(selectedFile);
    setError('');
    setImportResult(null);
    parseCSV(selectedFile);
  };

  const parseCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e?.target?.result;
        const lines = text?.split('\n')?.filter(line => line?.trim());

        if (lines?.length < 2) {
          setError('CSV file is empty or invalid');
          return;
        }

        const headers = lines?.[0]?.split(',')?.map(h => h?.trim()?.toLowerCase());
        const requiredHeaders = ['team_name', 'name', 'role', 'batting_style', 'bowling_style'];

        const missingHeaders = requiredHeaders?.filter(h => !headers?.includes(h));
        if (missingHeaders?.length > 0) {
          setError(`Missing required columns: ${missingHeaders?.join(', ')}`);
          return;
        }

        const data = [];
        for (let i = 1; i < lines?.length; i++) {
          const values = lines?.[i]?.split(',')?.map(v => v?.trim());
          const row = {};
          headers?.forEach((header, index) => {
            row[header] = values?.[index] || '';
          });

          if (row?.name && row?.role && row?.team_name) {
            data?.push(row);
          }
        }

        if (data?.length === 0) {
          setError('No valid player data found in CSV');
          return;
        }

        setPreview(data?.slice(0, 5));
        setError('');
      } catch (err) {
        setError('Failed to parse CSV file. Please check the format.');
        console.error('CSV parsing error:', err);
      }
    };
    reader?.readAsText(file);
  };

  const handleImport = async () => {
    if (!file || preview?.length === 0) return;

    setIsProcessing(true);
    setImportResult(null);

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e?.target?.result;
        const lines = text?.split('\n')?.filter(line => line?.trim());
        const headers = lines?.[0]?.split(',')?.map(h => h?.trim()?.toLowerCase());

        const allPlayers = [];
        for (let i = 1; i < lines?.length; i++) {
          const values = lines?.[i]?.split(',')?.map(v => v?.trim());
          const row = {};
          headers?.forEach((header, index) => {
            row[header] = values?.[index] || '';
          });

          if (!row?.name || !row?.role || !row?.team_name) continue;

          const validRoles = ['batter', 'batsman', 'bowler', 'all-rounder', 'wicket-keeper'];
          const role = row?.role?.toLowerCase()?.replace(/\s+/g, '-');

          if (!validRoles?.includes(role)) {
            console.warn(`Skipping player ${row?.name}: Invalid role ${row?.role}`);
            continue;
          }

          const roleMap = {
            'batter': 'Batter',
            'batsman': 'Batter',
            'bowler': 'Bowler',
            'all-rounder': 'All-rounder',
            'wicket-keeper': 'Wicket Keeper'
          };

          allPlayers?.push({
            name: row?.name,
            teamName: row?.team_name,
            role: roleMap?.[role],
            battingStyle: row?.batting_style || 'right-hand',
            bowlingStyle: row?.bowling_style || 'none',
            jerseyNumber: row?.jersey_number ? parseInt(row?.jersey_number) : null
          });
        }

        if (allPlayers?.length === 0) {
          setError('No valid players found. Please check role values.');
          setIsProcessing(false);
          return;
        }

        // Build a lookup map: team name (lowercase) -> team object
        const teamMap = {};
        teams?.forEach(team => {
          teamMap[team?.name?.toLowerCase()] = team;
        });

        // Fetch all existing players grouped by team to check duplicates
        const existingPlayersByTeam = {};
        const uniqueTeamNames = [...new Set(allPlayers?.map(p => p?.teamName?.toLowerCase()))];

        for (const teamNameLower of uniqueTeamNames) {
          const team = teamMap?.[teamNameLower];
          if (team) {
            try {
              const existing = await playersService?.getByTeam(team?.id);
              existingPlayersByTeam[team?.id] = new Set(
                existing?.map(p => p?.name?.toLowerCase()?.trim())
              );
            } catch {
              existingPlayersByTeam[team?.id] = new Set();
            }
          }
        }

        let added = 0;
        let skipped = 0;
        let teamNotFound = 0;
        const errors = [];

        for (const player of allPlayers) {
          const team = teamMap?.[player?.teamName?.toLowerCase()];

          if (!team) {
            teamNotFound++;
            errors?.push(`Team not found: "${player?.teamName}" (player: ${player?.name})`);
            continue;
          }

          const existingNames = existingPlayersByTeam?.[team?.id] || new Set();
          if (existingNames?.has(player?.name?.toLowerCase()?.trim())) {
            skipped++;
            continue;
          }

          try {
            await playersService?.create({
              name: player?.name,
              teamId: team?.id,
              role: player?.role,
              battingStyle: player?.battingStyle,
              bowlingStyle: player?.bowlingStyle,
              jerseyNumber: player?.jerseyNumber
            });
            // Add to existing set to prevent duplicates within the same import
            existingNames?.add(player?.name?.toLowerCase()?.trim());
            existingPlayersByTeam[team?.id] = existingNames;
            added++;
          } catch (err) {
            console.error(`Failed to add player ${player?.name}:`, err);
            errors?.push(`Failed to add: ${player?.name}`);
          }
        }

        setImportResult({ added, skipped, teamNotFound, errors });
        onRefresh?.();
      } catch (err) {
        setError('Failed to import players. Please try again.');
        console.error('Import error:', err);
      } finally {
        setIsProcessing(false);
      }
    };

    reader?.readAsText(file);
  };

  const handleClose = () => {
    setFile(null);
    setError('');
    setPreview([]);
    setIsProcessing(false);
    setImportResult(null);
    onClose();
  };

  const downloadTemplate = () => {
    const headers = 'team_name,name,role,batting_style,bowling_style,jersey_number';
    const example1 = 'India,Virat Kohli,batter,right-hand,right-arm-medium,18';
    const example2 = 'India,Jasprit Bumrah,bowler,right-hand,right-arm-fast,93';
    const example3 = 'Australia,Steve Smith,batter,right-hand,right-arm-medium,49';
    const example4 = 'Mumbai Indians,Rohit Sharma,batter,right-hand,right-arm-medium,45';
    const csv = `${headers}\n${example1}\n${example2}\n${example3}\n${example4}`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'global_players_template.csv';
    a?.click();
    window.URL?.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1200] p-4">
      <div className="bg-card border border-border rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between">
          <div>
            <h3 className="text-lg md:text-xl font-semibold text-foreground">Global Player Import</h3>
            <p className="text-sm text-muted-foreground mt-1">Import players for country teams via CSV</p>
          </div>
          <button
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-4 md:p-6 space-y-6">
          {/* Info Box */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-blue-600 dark:text-blue-400 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">CSV Format Requirements</h4>
                <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                  <li>• Required columns: <strong>team_name</strong>, name, role, batting_style, bowling_style, jersey_number</li>
                  <li>• <strong>team_name</strong> must match an existing <strong>country team</strong> name exactly (case-insensitive)</li>
                  <li>• League teams are not supported — use the team roster for league player management</li>
                  <li>• Role values: batter, batsman, bowler, all-rounder, wicket-keeper</li>
                  <li>• Batting style: right-hand, left-hand</li>
                  <li>• Bowling style: none, right-arm-fast, left-arm-fast, right-arm-medium, left-arm-medium, right-arm-spin, left-arm-spin</li>
                  <li>• If a player already exists in the team, they will be skipped</li>
                </ul>
                <Button
                  variant="link"
                  onClick={downloadTemplate}
                  iconName="Download"
                  iconPosition="left"
                  className="mt-2 text-blue-600 dark:text-blue-400 p-0 h-auto"
                >
                  Download Template CSV
                </Button>
              </div>
            </div>
          </div>

          {/* Available Teams */}
          {teams?.length > 0 && (
            <div>
              <p className="text-sm font-medium text-foreground mb-2">Available Country Teams ({teams?.length})</p>
              <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                {teams?.map(team => (
                  <span
                    key={team?.id}
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-muted text-muted-foreground rounded-full"
                  >
                    <Icon name={team?.teamType === 'league' ? 'Trophy' : 'Flag'} className="w-3 h-3" />
                    {team?.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Select CSV File
            </label>
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary transition-colors">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
                id="global-csv-upload"
              />
              <label
                htmlFor="global-csv-upload"
                className="cursor-pointer flex flex-col items-center gap-3"
              >
                <Icon name="Upload" size={48} className="text-muted-foreground" />
                <div>
                  <p className="text-foreground font-medium">Click to upload CSV file</p>
                  <p className="text-sm text-muted-foreground mt-1">or drag and drop</p>
                </div>
              </label>
            </div>
            {file && (
              <div className="mt-3 flex items-center gap-2 text-sm text-foreground">
                <Icon name="FileText" size={16} />
                <span>{file?.name}</span>
              </div>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={20} className="text-red-600 dark:text-red-400 mt-0.5" />
                <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
              </div>
            </div>
          )}

          {/* Import Result */}
          {importResult && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2">
                <Icon name="CheckCircle" size={20} className="text-green-600 dark:text-green-400" />
                <p className="font-medium text-green-900 dark:text-green-100">Import Complete</p>
              </div>
              <div className="text-sm text-green-800 dark:text-green-200 space-y-1">
                <p>✅ Added: <strong>{importResult?.added}</strong> players</p>
                <p>⏭️ Skipped (already exist): <strong>{importResult?.skipped}</strong> players</p>
                {importResult?.teamNotFound > 0 && (
                  <p>⚠️ Team not found: <strong>{importResult?.teamNotFound}</strong> players</p>
                )}
              </div>
              {importResult?.errors?.length > 0 && (
                <div className="mt-2 text-xs text-red-700 dark:text-red-300 space-y-0.5">
                  {importResult?.errors?.slice(0, 5)?.map((err, i) => (
                    <p key={i}>• {err}</p>
                  ))}
                  {importResult?.errors?.length > 5 && (
                    <p>...and {importResult?.errors?.length - 5} more</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Preview */}
          {preview?.length > 0 && !importResult && (
            <div>
              <h4 className="font-medium text-foreground mb-3">Preview (First 5 rows)</h4>
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-foreground">Team</th>
                        <th className="px-3 py-2 text-left font-medium text-foreground">Name</th>
                        <th className="px-3 py-2 text-left font-medium text-foreground">Role</th>
                        <th className="px-3 py-2 text-left font-medium text-foreground">Batting</th>
                        <th className="px-3 py-2 text-left font-medium text-foreground">Jersey #</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview?.map((row, index) => (
                        <tr key={index} className="border-t border-border">
                          <td className="px-3 py-2 text-foreground">{row?.team_name}</td>
                          <td className="px-3 py-2 text-foreground">{row?.name}</td>
                          <td className="px-3 py-2 text-muted-foreground">{row?.role}</td>
                          <td className="px-3 py-2 text-muted-foreground">{row?.batting_style}</td>
                          <td className="px-3 py-2 text-muted-foreground">{row?.jersey_number || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-card border-t border-border p-4 md:p-6 flex flex-col sm:flex-row gap-3 justify-end">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isProcessing}
            className="w-full sm:w-auto"
          >
            {importResult ? 'Close' : 'Cancel'}
          </Button>
          {!importResult && (
            <Button
              variant="default"
              onClick={handleImport}
              disabled={!file || preview?.length === 0 || isProcessing}
              iconName={isProcessing ? 'Loader' : 'Upload'}
              iconPosition="left"
              className="w-full sm:w-auto"
            >
              {isProcessing ? 'Importing...' : 'Import Players'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GlobalImportPlayersModal;
